import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { QRCodeCanvas } from "qrcode.react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import {
  Building2, BarChart2, QrCode, Download, Users, Eye, MousePointerClick,
  CalendarCheck, CheckCircle2, Loader, Save, Printer, ClipboardList, Wifi,
  Image as ImageIcon, UploadCloud, Star, ExternalLink,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const profileSchema = z.object({
  name: z.string().min(2, "Facility name is required"),
  address: z.string().min(4, "Address is required"),
  city: z.string().min(2, "City is required"),
  courts: z.coerce.number().int("Must be a whole number").min(0, "Cannot be negative").max(500, "Too many"),
  fees: z.string().max(120, "Keep it under 120 characters").optional().or(z.literal("")),
  phone: z.string().max(40).optional().or(z.literal("")),
  website: z.string().url("Enter a valid URL (https://…)").optional().or(z.literal("")),
  booking_instructions: z.string().max(400, "Keep under 400 characters").optional().or(z.literal("")),
});

function StatCard({ icon: Icon, label, value, accent = "text-white", testid }) {
  return (
    <div className="bg-[#161B28] border border-white/8 rounded-xl p-4 dark-section" data-testid={testid}>
      <div className="flex items-center gap-2 text-gray-500 mb-1.5">
        <Icon size={14} aria-hidden="true" />
        <p className="text-xs">{label}</p>
      </div>
      <p className={`text-2xl font-bold ${accent}`}>{value}</p>
    </div>
  );
}

// ── Profile Tab ──────────────────────────────────────────────────────────────
function ProfileTab({ facility, taxonomy, onSaved }) {
  const [sports, setSports] = useState(facility.sport || []);
  const [amenities, setAmenities] = useState(facility.amenities || []);
  const [indoor, setIndoor] = useState(!!facility.indoor);
  const [isPublic, setIsPublic] = useState(facility.is_public !== false);
  const [saving, setSaving] = useState(false);
  const [savedOk, setSavedOk] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      name: facility.name || "", address: facility.address || "", city: facility.city || "",
      courts: facility.courts ?? 0, fees: facility.fees || "", phone: facility.phone || "",
      website: facility.website || "", booking_instructions: facility.booking_instructions || "",
    },
  });

  const toggle = (list, setList, v) =>
    setList(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  const onSubmit = async (values) => {
    setSaving(true); setSavedOk(false);
    try {
      const body = { ...values, sport: sports, amenities, indoor, is_public: isPublic };
      const r = await fetch(`${API}/facility/me`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(body),
      });
      if (r.ok) { const d = await r.json(); setSavedOk(true); onSaved(d); setTimeout(() => setSavedOk(false), 2500); }
    } finally { setSaving(false); }
  };

  const inputCls = "w-full bg-[#1A2035] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-green/40";
  const errCls = "mt-1 text-xs text-red-400";

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 max-w-2xl" data-testid="facility-profile-form" noValidate>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="name" className="text-xs text-gray-400 uppercase tracking-wider block mb-1.5">Facility Name</label>
          <input id="name" className={inputCls} {...register("name")} data-testid="facility-name-input" aria-invalid={!!errors.name} />
          {errors.name && <p className={errCls} role="alert">{errors.name.message}</p>}
        </div>
        <div>
          <label htmlFor="city" className="text-xs text-gray-400 uppercase tracking-wider block mb-1.5">City</label>
          <input id="city" className={inputCls} {...register("city")} data-testid="facility-city-input" aria-invalid={!!errors.city} />
          {errors.city && <p className={errCls} role="alert">{errors.city.message}</p>}
        </div>
      </div>

      <div>
        <label htmlFor="address" className="text-xs text-gray-400 uppercase tracking-wider block mb-1.5">Address</label>
        <input id="address" className={inputCls} {...register("address")} data-testid="facility-address-input" aria-invalid={!!errors.address} />
        {errors.address && <p className={errCls} role="alert">{errors.address.message}</p>}
      </div>

      <div>
        <span className="text-xs text-gray-400 uppercase tracking-wider block mb-1.5">Sports Offered</span>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Sports offered">
          {(taxonomy.sports || []).map((s) => (
            <button key={s.value} type="button" onClick={() => toggle(sports, setSports, s.value)}
              aria-pressed={sports.includes(s.value)}
              className={`px-3 py-1.5 rounded-lg text-xs border transition-colors ${sports.includes(s.value) ? "bg-brand-green/10 border-brand-green/40 text-brand-green" : "border-white/10 text-gray-400 hover:text-white"}`}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="courts" className="text-xs text-gray-400 uppercase tracking-wider block mb-1.5">Number of Courts</label>
          <input id="courts" type="number" className={inputCls} {...register("courts")} data-testid="facility-courts-input" aria-invalid={!!errors.courts} />
          {errors.courts && <p className={errCls} role="alert">{errors.courts.message}</p>}
        </div>
        <div>
          <label htmlFor="fees" className="text-xs text-gray-400 uppercase tracking-wider block mb-1.5">Fees</label>
          <input id="fees" className={inputCls} placeholder="e.g. $3/hour" {...register("fees")} data-testid="facility-fees-input" />
          {errors.fees && <p className={errCls} role="alert">{errors.fees.message}</p>}
        </div>
      </div>

      <div>
        <span className="text-xs text-gray-400 uppercase tracking-wider block mb-1.5">Amenities</span>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Amenities">
          {(taxonomy.facility_amenities || []).map((a) => (
            <button key={a} type="button" onClick={() => toggle(amenities, setAmenities, a)}
              aria-pressed={amenities.includes(a)}
              className={`px-3 py-1.5 rounded-lg text-xs border transition-colors ${amenities.includes(a) ? "bg-brand-green/10 border-brand-green/40 text-brand-green" : "border-white/10 text-gray-400 hover:text-white"}`}>
              {a}
            </button>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="phone" className="text-xs text-gray-400 uppercase tracking-wider block mb-1.5">Phone</label>
          <input id="phone" className={inputCls} {...register("phone")} data-testid="facility-phone-input" />
          {errors.phone && <p className={errCls} role="alert">{errors.phone.message}</p>}
        </div>
        <div>
          <label htmlFor="website" className="text-xs text-gray-400 uppercase tracking-wider block mb-1.5">Website</label>
          <input id="website" className={inputCls} placeholder="https://…" {...register("website")} data-testid="facility-website-input" aria-invalid={!!errors.website} />
          {errors.website && <p className={errCls} role="alert">{errors.website.message}</p>}
        </div>
      </div>

      <div>
        <label htmlFor="booking" className="text-xs text-gray-400 uppercase tracking-wider block mb-1.5">Booking Instructions</label>
        <textarea id="booking" rows={3} className={`${inputCls} resize-none`} {...register("booking_instructions")} data-testid="facility-booking-input" />
        {errors.booking_instructions && <p className={errCls} role="alert">{errors.booking_instructions.message}</p>}
      </div>

      <div className="flex flex-wrap gap-4">
        <button type="button" onClick={() => setIndoor(!indoor)} aria-pressed={indoor}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm border transition-colors ${indoor ? "bg-brand-green/10 border-brand-green/40 text-brand-green" : "border-white/10 text-gray-400"}`}
          data-testid="facility-indoor-toggle">
          <Wifi size={14} /> {indoor ? "Indoor" : "Outdoor"}
        </button>
        <button type="button" onClick={() => setIsPublic(!isPublic)} aria-pressed={isPublic}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm border transition-colors ${isPublic ? "bg-brand-green/10 border-brand-green/40 text-brand-green" : "border-white/10 text-gray-400"}`}
          data-testid="facility-public-toggle">
          <Eye size={14} /> {isPublic ? "Publicly listed" : "Hidden"}
        </button>
      </div>

      <div className="flex items-center gap-3 pt-2">
        <button type="submit" disabled={saving}
          className="inline-flex items-center gap-2 px-6 py-3 bg-brand-green text-brand-navy font-bold rounded-xl hover:bg-brand-green/90 transition-colors disabled:opacity-50"
          data-testid="facility-save-btn">
          {saving ? <Loader size={15} className="animate-spin" /> : <Save size={15} />}
          {saving ? "Saving…" : "Save Changes"}
        </button>
        {savedOk && <span className="inline-flex items-center gap-1.5 text-sm text-brand-green" data-testid="facility-saved-msg"><CheckCircle2 size={15} /> Saved — public listing updated</span>}
      </div>
    </form>
  );
}

// ── QR Tab ───────────────────────────────────────────────────────────────────
function QrTab({ facility }) {
  const checkinUrl = `${window.location.origin}/facility/checkin/${facility.id}`;
  return (
    <div className="max-w-md" data-testid="facility-qr-tab">
      <p className="text-gray-400 text-sm mb-5">Display this QR code at your venue. Logged-in players who scan it are instantly checked in to your next open session.</p>
      <div className="bg-white rounded-2xl p-6 flex flex-col items-center gap-4">
        <QRCodeCanvas value={checkinUrl} size={220} level="M" includeMargin data-testid="facility-qr-code" />
        <p className="text-xs text-gray-500 break-all text-center">{checkinUrl}</p>
      </div>
      <button onClick={() => window.print()}
        className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 border border-white/15 text-white rounded-xl hover:border-white/25 transition-colors text-sm"
        data-testid="facility-print-qr-btn">
        <Printer size={15} /> Print QR poster
      </button>
    </div>
  );
}

// ── Check-ins Tab ─────────────────────────────────────────────────────────────
function CheckinsTab({ checkins, onExport }) {
  return (
    <div data-testid="facility-checkins-tab">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-400">{checkins.length} check-in record{checkins.length !== 1 ? "s" : ""}</p>
        <button onClick={() => onExport("checkins")}
          className="inline-flex items-center gap-2 px-4 py-2 bg-brand-orange text-white text-sm font-bold rounded-xl hover:bg-brand-orange/90 transition-colors"
          data-testid="export-checkins-btn">
          <Download size={14} /> Export CSV
        </button>
      </div>
      {checkins.length === 0 ? (
        <div className="text-center py-16 bg-[#161B28] border border-white/8 rounded-2xl dark-section">
          <ClipboardList size={32} className="text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">No check-ins yet. Share your QR code to start.</p>
        </div>
      ) : (
        <div className="bg-[#161B28] border border-white/8 rounded-2xl overflow-hidden dark-section">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b border-white/8">
                <th className="px-4 py-3 font-medium">Player</th>
                <th className="px-4 py-3 font-medium">Session</th>
                <th className="px-4 py-3 font-medium">Checked in</th>
              </tr>
            </thead>
            <tbody>
              {checkins.map((c) => (
                <tr key={c.id} className="border-b border-white/5 last:border-0" data-testid="checkin-row">
                  <td className="px-4 py-3 text-white">{c.user_name}</td>
                  <td className="px-4 py-3 text-gray-400">{c.session_title || "Walk-in"}</td>
                  <td className="px-4 py-3 text-gray-400">{new Date(c.checked_in_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Spotlight Tab ─────────────────────────────────────────────────────────────
function SpotlightTab({ facility, onSaved }) {
  const [uploading, setUploading] = useState(false);
  const [cacheBust, setCacheBust] = useState(Date.now());
  const [hasPhoto, setHasPhoto] = useState(facility.has_photo);
  const [bookingUrl, setBookingUrl] = useState(facility.booking_url || "");
  const [spotlight, setSpotlight] = useState(facility.spotlight !== false);
  const [saving, setSaving] = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  const [err, setErr] = useState("");

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API}/facility/me/photo`, { method: "POST", credentials: "include", body: fd });
      if (r.ok) { setHasPhoto(true); setCacheBust(Date.now()); }
      else { const d = await r.json().catch(() => ({})); setErr(d.detail || "Upload failed"); }
    } catch { setErr("Upload failed"); }
    finally { setUploading(false); }
  };

  const saveSettings = async () => {
    setSaving(true); setSavedOk(false);
    try {
      const r = await fetch(`${API}/facility/me`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ booking_url: bookingUrl, spotlight }),
      });
      if (r.ok) { const d = await r.json(); onSaved(d); setSavedOk(true); setTimeout(() => setSavedOk(false), 2500); }
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-xl space-y-6" data-testid="facility-spotlight-tab">
      <p className="text-gray-400 text-sm">Feature a photo and a "Book Now" link so players discover your venue in the public directory.</p>

      {/* Photo */}
      <div>
        <span className="text-xs text-gray-400 uppercase tracking-wider block mb-2">Venue Photo</span>
        <div className="rounded-2xl border border-white/10 overflow-hidden bg-[#1A2035] dark-section">
          {hasPhoto ? (
            <img src={`${API}/facility/photo/${facility.id}?t=${cacheBust}`} alt={facility.name} className="w-full h-48 object-cover" data-testid="facility-photo-preview" />
          ) : (
            <div className="w-full h-48 flex flex-col items-center justify-center text-gray-600">
              <ImageIcon size={32} className="mb-2" /> <span className="text-sm">No photo yet</span>
            </div>
          )}
        </div>
        <label className="mt-3 inline-flex items-center gap-2 px-4 py-2.5 border border-white/15 text-white text-sm font-medium rounded-xl hover:border-white/25 transition-colors cursor-pointer" data-testid="facility-photo-upload-label">
          {uploading ? <Loader size={15} className="animate-spin" /> : <UploadCloud size={15} />}
          {uploading ? "Uploading…" : hasPhoto ? "Replace photo" : "Upload photo"}
          <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={upload} data-testid="facility-photo-input" />
        </label>
        {err && <p className="mt-2 text-xs text-red-400">{err}</p>}
      </div>

      {/* Book Now link */}
      <div>
        <label htmlFor="booking_url" className="text-xs text-gray-400 uppercase tracking-wider block mb-1.5">"Book Now" Link</label>
        <input id="booking_url" value={bookingUrl} onChange={(e) => setBookingUrl(e.target.value)} placeholder="https://your-booking-page.com"
          className="w-full bg-[#1A2035] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-green/40 dark-section"
          data-testid="facility-booking-url-input" />
      </div>

      {/* Spotlight toggle */}
      <button type="button" onClick={() => setSpotlight(!spotlight)} aria-pressed={spotlight}
        className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm border transition-colors ${spotlight ? "bg-brand-green/10 border-brand-green/40 text-brand-green" : "border-white/10 text-gray-400"}`}
        data-testid="facility-spotlight-toggle">
        <Star size={14} /> {spotlight ? "Featured in directory" : "Not featured"}
      </button>

      <div className="flex items-center gap-3">
        <button onClick={saveSettings} disabled={saving}
          className="inline-flex items-center gap-2 px-6 py-3 bg-brand-green text-brand-navy font-bold rounded-xl hover:bg-brand-green/90 transition-colors disabled:opacity-50"
          data-testid="spotlight-save-btn">
          {saving ? <Loader size={15} className="animate-spin" /> : <Save size={15} />} Save Spotlight
        </button>
        {savedOk && <span className="inline-flex items-center gap-1.5 text-sm text-brand-green"><CheckCircle2 size={15} /> Saved</span>}
      </div>
    </div>
  );
}

// ── Main Portal ────────────────────────────────────────────────────────────────
export default function FacilityPortalPage() {
  const { user } = useAuth();
  const [facility, setFacility] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [checkins, setCheckins] = useState([]);
  const [taxonomy, setTaxonomy] = useState({});
  const [tab, setTab] = useState("dashboard");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadAll = useCallback(async () => {
    try {
      const [fr, mr, cr, tr] = await Promise.all([
        fetch(`${API}/facility/me`, { credentials: "include" }),
        fetch(`${API}/facility/me/metrics`, { credentials: "include" }),
        fetch(`${API}/facility/me/checkins`, { credentials: "include" }),
        fetch(`${API}/taxonomy`),
      ]);
      if (!fr.ok) { setError("No facility is linked to your account."); setLoading(false); return; }
      setFacility(await fr.json());
      if (mr.ok) setMetrics(await mr.json());
      if (cr.ok) setCheckins((await cr.json()).checkins || []);
      if (tr.ok) setTaxonomy(await tr.json());
    } catch { setError("Failed to load facility portal."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const exportCsv = async (type) => {
    const r = await fetch(`${API}/facility/me/export?type=${type}`, { credentials: "include" });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `facility_${type}.csv`; document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  if (!user) return <div className="min-h-screen bg-[#0F1629] pt-24 flex items-center justify-center px-4 dark-section"><p className="text-gray-400">Please <Link to="/login" className="text-brand-green hover:underline">log in</Link> to access the facility portal.</p></div>;
  if (loading) return <div className="min-h-screen bg-[#0F1629] pt-24 flex items-center justify-center dark-section"><Loader size={28} className="text-brand-green animate-spin" /></div>;
  if (error) return (
    <div className="min-h-screen bg-[#0F1629] pt-24 flex items-center justify-center px-4 dark-section" data-testid="facility-no-access">
      <div className="text-center max-w-sm">
        <Building2 size={40} className="text-gray-600 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-white mb-2">Facility Portal</h1>
        <p className="text-gray-400 text-sm">{error}</p>
      </div>
    </div>
  );

  const TABS = [
    { key: "dashboard", label: "Dashboard", icon: BarChart2 },
    { key: "profile", label: "Facility Profile", icon: Building2 },
    { key: "spotlight", label: "Spotlight", icon: Star },
    { key: "qr", label: "QR Check-in", icon: QrCode },
    { key: "checkins", label: "Check-ins", icon: ClipboardList },
  ];

  return (
    <div className="min-h-screen bg-[#0F1629] pt-20 pb-16 dark-section" data-testid="facility-portal">
      <div className="max-w-5xl mx-auto px-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <p className="text-xs text-brand-green font-semibold uppercase tracking-wider mb-1">Facility Partner Portal</p>
            <h1 className="text-2xl sm:text-3xl font-heading font-black text-white">{facility.name}</h1>
            <p className="text-gray-500 text-sm mt-0.5">{facility.address}</p>
          </div>
          <button onClick={() => exportCsv("metrics")}
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-white/15 text-white text-sm font-medium rounded-xl hover:border-white/25 transition-colors self-start"
            data-testid="export-metrics-btn">
            <Download size={15} /> Export metrics
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-white/8 mb-6 overflow-x-auto" role="tablist">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} role="tab" aria-selected={tab === t.key}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${tab === t.key ? "border-brand-green text-brand-green" : "border-transparent text-gray-400 hover:text-white"}`}
              data-testid={`facility-tab-${t.key}`}>
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "dashboard" && metrics && (
          <div data-testid="facility-dashboard-tab">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
              <StatCard icon={Eye} label="Profile Views" value={metrics.views} testid="stat-views" />
              <StatCard icon={MousePointerClick} label="Session Selections" value={metrics.selected} accent="text-brand-green" testid="stat-selected" />
              <StatCard icon={CalendarCheck} label="Sessions Requested" value={metrics.session_requested} testid="stat-requested" />
              <StatCard icon={CheckCircle2} label="Sessions Confirmed" value={metrics.session_confirmed} accent="text-brand-green" testid="stat-confirmed" />
              <StatCard icon={Users} label="Total Check-ins" value={metrics.checkins_total} testid="stat-checkins" />
              <StatCard icon={CalendarCheck} label="Sessions at Venue" value={metrics.sessions_total} testid="stat-sessions" />
            </div>
            <div className="bg-[#161B28] border border-white/8 rounded-2xl p-5 dark-section">
              <h2 className="text-sm font-bold text-white mb-4">Check-ins — last 7 days</h2>
              <div style={{ width: "100%", height: 220 }}>
                <ResponsiveContainer>
                  <BarChart data={metrics.trend} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff12" vertical={false} />
                    <XAxis dataKey="date" stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
                    <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: "#0F1629", border: "1px solid #ffffff20", borderRadius: 12, color: "#fff" }} cursor={{ fill: "#ffffff08" }} />
                    <Bar dataKey="checkins" fill="#2ED47A" radius={[6, 6, 0, 0]} maxBarSize={40} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {tab === "profile" && <ProfileTab facility={facility} taxonomy={taxonomy} onSaved={(d) => { setFacility(d); }} />}
        {tab === "spotlight" && <SpotlightTab facility={facility} onSaved={(d) => { setFacility(d); }} />}
        {tab === "qr" && <QrTab facility={facility} />}
        {tab === "checkins" && <CheckinsTab checkins={checkins} onExport={exportCsv} />}
      </div>
    </div>
  );
}
